<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import {
  ArrowRightIcon,
  BabyIcon,
  BusIcon,
  CalculatorIcon,
  DumbbellIcon,
  ExternalLink,
  ExternalLinkIcon,
  EyeIcon,
  EyeOffIcon,
  GiftIcon,
  GlassesIcon,
  GraduationCapIcon,
  Heart,
  HouseIcon,
  InfoIcon,
  LandmarkIcon,
  Laptop,
  PlaneIcon,
  Shield,
  ShieldPlusIcon,
  StarIcon,
  TreePalmIcon,
  TrophyIcon,
  UserPlusIcon,
  UsersIcon,
  Utensils,
} from "lucide-vue-next"
import { nextTick, ref } from "vue"

import Heading from "@/components/Heading.vue"
import BenefitAddedCard from "@/components/dashboard/BenefitAddedCard.vue"
import BenefitCard from "@/components/dashboard/BenefitCard.vue"
import MainHeading from "@/components/dashboard/MainHeading.vue"
import SalaryHint from "@/components/dashboard/SalaryHint.vue"
import SalaryProgress from "@/components/dashboard/SalaryProgress.vue"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/AppLayout.vue"
import {
  insurancePath,
  marketplacePath,
  mumsAndDadsPath,
  rootPath,
  studyPath,
  teleworkPath,
  transportCardPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

const benefits = [
  {
    price: "62,34",
    name: "Seguro\nde Salud",
    icon: Heart,
    dialogDesc: "For the basic health insurance",
    dialogContent:
      "• Cuida de tu salud y bienestar.\n• Reconocimiento médico anual\n• Sin carencia\n• Importe exento de IRPF hasta 500€ anuales",
    status: "approved",
  },
  {
    price: "45",
    name: "Compensación por trabajo a distancia",
    icon: Laptop,
    status: "approved",
  },
  {
    price: "204,42",
    name: "Tarjeta\nRestaurante",
    dialogContent:
      "• Paga tus comidas del día a día con una tarjeta prepago, válida en restaurantes y bares.\n• 100% exento de IRPF: más valor para ti.",
    icon: Utensils,
    status: "approved",
  },
  {
    price: "18,29",
    name: "Seguro de vida e incapacidad",
    dialogDesc:
      "Un apoyo económico para ti y tu familia en situaciones imprevistas.",
    dialogContent:
      "Garantías incluidas:\n- Incapacidad permanente total\n- Incapacidad permanente total por accidente\n- Incapacidad permanente por accidente de circulación\n- Fallecimiento por cualquier causa\n- Fallecimiento por accidente\n- Fallecimiento por accidente de circulación\n\nBeneficiarios en caso de defunción:\n1. Cónyuge (salvo que hubiese recaído sentencia de separación)\n2. Hijos a partos iguales\n3. Padres a partes iguales\n4. Herederos legales",
    icon: Shield,
    status: "approved",
  },

  {
    price: "195,71",
    name: "Cesta de nacimiento",
    dialogContent:
      "• Celebramos contigo la llegada de un nuevo miembro a tu familia\n• 195,71€",
    icon: GiftIcon,
    type: "Moms & Dads Club",
  },

  {
    price: "200",
    name: "Ayuda parental",
    icon: Shield,
    dialogDesc:
      "A partir del mes siguiente al nacimiento, recibirás automáticamente en nómina:",
    dialogContent:
      "• 200€/mes (primer hijo)\n• 300€/mes (segundo hijo)\n• 400€/mes (tercer hijo y siguientes)",
    type: "Moms & Dads Club",
  },
]

const newBenefits = [
  {
    name: "Ayuda de Teletrabajo",
    icon: Laptop,
    status: "pending",
    path: teleworkPath(),
  },
  {
    name: "Ayuda de Estudios",
    icon: GraduationCapIcon,
    status: "approved",
    path: studyPath(),
  },
  {
    name: "Vivofacil",
    type: "Marketplace",
    icon: HouseIcon,
    path: marketplacePath(),
  },
  {
    name: "Wellhub",
    type: "Marketplace",
    icon: DumbbellIcon,
    path: marketplacePath(),
  },
  {
    name: "Taxdown",
    type: "Marketplace",
    icon: LandmarkIcon,
    path: marketplacePath(),
  },
]

const salaryBenefits = [
  {
    name: "Adhiere\na familiares",
    type: "Seguro",
    icon: UserPlusIcon,
    status: "approved",
    path: insurancePath(),
  },
  {
    name: "Amplia con cobertura reembolso",
    type: "Seguro",
    icon: ShieldPlusIcon,
    status: "approved",
    path: insurancePath(),
  },
  {
    name: "Amplia a familiares con cobertura reembolso",
    type: "Seguro",
    icon: UsersIcon,
    path: insurancePath(),
  },
  {
    name: "Pago de guardería",
    type: "Moms & Dads Club",
    icon: BabyIcon,
    path: mumsAndDadsPath(),
  },
  {
    name: "Tarjeta\ntransporte",
    icon: BusIcon,
    status: "pending",
    path: transportCardPath(),
  },
]

const discountBenefits = [
  {
    name: "Descuento en tus vacaciones",
    type: "Vidacaixa",
    icon: TreePalmIcon,
    path: marketplacePath(),
  },
  {
    name: "Descuento en tus gafas",
    type: "Compensación",
    icon: GlassesIcon,
    path: marketplacePath(),
  },
]

const awardsBenefits = [
  {
    name: "President’s Club",
    icon: TrophyIcon,
    dialogContent:
      "Asistirás a un evento exclusivo organizado por Global Payments en el que podrás participar en todo tipo de actividades, con todos los gastos pagados para ti y tu acompañante.",
    path: teleworkPath(),
  },
  {
    name: "Circle of Excelente",
    dialogContent:
      "Recompensa económica (350$) + 1.000€ de crédito para gastar en un viaje + 3 días adicionales de vacaciones.",
    icon: StarIcon,
    path: studyPath(),
  },
  {
    name: "Gold Trip",
    dialogContent:
      "Travel Token para organizar un viaje premium a tu elección.",
    icon: PlaneIcon,
    path: studyPath(),
  },
]

const faq = [
  {
    title: "¿Qué es la retribución directa?",
    description:
      "Es el dinero que recibes directamente en tu nómina. Esta categoría incluye tu salario base y el bonus que cobras según el desarrollo de tus objetivos.",
  },
  {
    title: "¿Qué es el ahorro fiscal?",
    description:
      "El ahorro fiscal te permite pagar ciertos servicios con tu sueldo antes de que se apliquen los impuestos. Los beneficios de esta categoría incluyen, si los tienes activos, el tiquet guardería y la tarjeta de transporte.",
  },
  {
    title: "¿Qué son las ventajas para el empleado?",
    description:
      "Aquí se incluye el coste de los servicios que Comercia paga por ti. Los beneficios por defecto incluyen el seguro de salud, la tarjeta restaurante, el seguro de vida y la ayuda para suministros por teletrabajo.\n\nAdemás, si tienes ayuda parental, has solicitado equipamiento para el teletrabajo o cuentas con ayuda de estudios, también se incluyen en este apartado.\n\nInformación sobre los límites legales de la compensación flexible:\n\n• Tarjeta transporte: máximo 125€/mes o 1.500€/año\n• Tarjeta restaurante: máximo 11€/día o 2.400€/año\n• El Seguro de salud: máximo 1.500€ por asegurado externo",
  },
]

const isBlurred = ref(false)

const showSalaryHint = ref(false)
const highlightedIndex = ref<number | null>(null)
const faqRefs = ref<HTMLElement[]>([])

async function highlightFaq(index: number) {
  await nextTick()

  highlightedIndex.value = index

  faqRefs.value[index]?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  })

  setTimeout(() => {
    highlightedIndex.value = null
  }, 2000)
}

const showBonus = ref(true)

defineProps<{
  name?: string
}>()

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: rootPath(),
  },
]
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="relative px-6 pt-4 pb-10">
      <button
        class="text-primary fixed top-5 right-[calc((100%-1210px)/2)] z-20 flex h-fit transform cursor-pointer items-center gap-2 rounded-sm bg-[#F4F4FF] px-4 py-2 text-sm"
        @click="isBlurred = !isBlurred"
      >
        <EyeIcon v-if="isBlurred" class="size-4" />
        <EyeOffIcon v-else class="size-4" />
        Ocultar datos
      </button>

      <div class="flex justify-between">
        <MainHeading :name="name" :title="`¡Hola,`" />
      </div>

      <div class="mt-10">
        <Heading primary-color title="Tu Compensación Total 2026" />

        <div class="border-border mt-4 rounded-md border p-4.5">
          <div class="mb-6 flex items-center justify-between">
            <div>
              <p class="text-muted-foreground">Compensación total anual</p>

              <p
                class="text-primary mt-4 mb-6 text-5xl font-bold"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                {{ showBonus ? "36.000,00" : "33.000,00"
                }}<span class="text-2xl font-bold">€</span>
              </p>
              <div class="flex items-center gap-3">
                <Checkbox v-model="showBonus" id="terms" />
                <Label class="text-primary -ml-1" for="terms"
                  >Bonus estimado</Label
                >
              </div>
            </div>

            <div
              class="flex h-fit w-80 cursor-pointer flex-col justify-center gap-2 rounded-sm bg-[#F4F4FF] p-4"
            >
              <div class="flex gap-2">
                <CalculatorIcon
                  class="text-primary mt-1 -ml-1 size-8 shrink-0"
                />

                <p class="text-primary text-sm">
                  Los porcentajes de ahorro fiscal son estimados y pueden variar
                  según tu situación personal.
                </p>
              </div>
              <div class="flex items-center justify-between">
                <p class="text-primary underline">
                  Calculadora de Ahorro Fiscal
                </p>

                <ArrowRightIcon class="text-primary mt-auto ml-auto shrink-0" />
              </div>
            </div>
          </div>

          <div class="flex h-3.5 w-full overflow-hidden rounded-full">
            <div
              class="h-full bg-[#1212fe]"
              :class="showBonus ? 'w-[83.3%]' : 'w-[90.9%]'"
            ></div>
            <div v-if="showBonus" class="h-full w-[8.3%] bg-[#F4F4FF]"></div>
            <div
              class="h-full bg-[#c2410b]"
              :class="showBonus ? 'w-[5.0%]' : 'w-[5.5%]'"
            ></div>
            <div
              class="h-full bg-[#118c6e]"
              :class="showBonus ? 'w-[3.4%]' : 'w-[3.6%]'"
            ></div>
          </div>

          <hr class="mt-6 border-black/10" />

          <div
            class="mt-6 grid gap-4"
            :class="showBonus ? 'grid-cols-4' : 'grid-cols-3'"
          >
            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2">
                <span class="min-h-4 min-w-4 rounded-full bg-[#1212fe]" />

                <div class="flex flex-col">
                  <p class="text-muted-foreground text-sm uppercase">SALARIO</p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  class="text-muted-foreground -mt-[2px] -ml-3 hidden cursor-pointer p-0 hover:bg-transparent"
                  @click="highlightFaq(0)"
                >
                  <InfoIcon class="text-muted-foreground size-3.5" />
                </Button>
              </div>

              <p
                class="text-xl font-bold opacity-80"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                {{ showBonus ? "30.000,00" : "30.000,00"
                }}<span class="text-sm">€</span>
              </p>

              <p
                class="text-muted-foreground -mt-2 text-sm"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                <span class="text-black">{{
                  showBonus ? "83.3%" : "90.9%"
                }}</span>
                · 2.500 €/mes
              </p>
            </div>

            <div v-if="showBonus" class="flex flex-col gap-2">
              <div class="flex items-center gap-2">
                <span class="min-h-4 min-w-4 rounded-full bg-[#F4F4FF]" />

                <div class="flex flex-col">
                  <p class="text-muted-foreground text-sm uppercase">
                    BONUS TARGET
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  class="text-muted-foreground pointer-events-none -mt-[2px] -ml-3 hidden p-0 opacity-0 hover:bg-transparent"
                >
                  <InfoIcon class="text-muted-foreground size-3.5" />
                </Button>
              </div>

              <p
                class="text-xl font-bold opacity-80"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                3.000,00<span class="text-sm">€</span>
              </p>

              <p
                class="text-muted-foreground -mt-2 text-sm"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                <span class="text-black">8.3%</span>
              </p>
            </div>

            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2">
                <span class="min-h-3.5 min-w-3.5 rounded-full bg-[#c2410b]" />

                <p class="text-muted-foreground text-sm uppercase">
                  Ahorro Fiscal
                </p>

                <Button
                  variant="ghost"
                  size="icon"
                  class="text-muted-foreground -mt-[2px] -ml-3 hidden cursor-pointer p-0 hover:bg-transparent"
                  @click="highlightFaq(1)"
                >
                  <InfoIcon class="text-muted-foreground size-3.5" />
                </Button>
              </div>

              <p
                class="text-xl font-bold opacity-80"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                1.800,00<span class="text-sm">€</span>
              </p>

              <p
                class="text-muted-foreground -mt-2 text-sm"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                <span class="text-black">{{
                  showBonus ? "5.0%" : "5.5%"
                }}</span>
                · 150 €/mes
              </p>
            </div>

            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2">
                <span class="min-h-4 min-w-4 rounded-full bg-[#118c6e]" />

                <p class="text-muted-foreground text-sm uppercase">
                  BENEFICIOS
                </p>

                <Button
                  variant="ghost"
                  size="icon"
                  class="text-muted-foreground -mt-[2px] -ml-3 hidden cursor-pointer p-0 hover:bg-transparent"
                  @click="highlightFaq(2)"
                >
                  <InfoIcon class="text-muted-foreground size-3.5" />
                </Button>
              </div>

              <p
                class="text-xl font-bold opacity-80"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                1.200,00<span class="text-sm">€</span>
              </p>

              <p
                class="text-muted-foreground -mt-2 text-sm"
                :class="{
                  'user-select-none pointer-events-none blur-sm transition-all duration-300':
                    isBlurred,
                }"
              >
                <span class="text-black">{{
                  showBonus ? "3.4%" : "3.6%"
                }}</span>
                · 100 €/mes
              </p>
            </div>
          </div>
        </div>
      </div>

      <Heading class="mt-10 mb-4" primary-color title="Tus beneficios" />

      <div class="border-border rounded-md border p-4.5">
        <p class="text-muted-foreground">Tus beneficios desde primer día</p>

        <div class="mt-2 grid grid-cols-4 gap-4">
          <Dialog v-for="(benefit, index) in benefits" :key="index">
            <DialogTrigger
              class="h-full"
              :class="{ 'pointer-events-none': !benefit.dialogContent }"
            >
              <BenefitCard
                :status="benefit.status"
                :price="benefit.price"
                :name="benefit.name"
                :type="benefit.type"
              >
                <template #icon>
                  <component :is="benefit.icon" class="size-4" />
                </template>
              </BenefitCard>
            </DialogTrigger>

            <DialogContent class="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle class="text-primary text-lg">{{
                  benefit.name
                }}</DialogTitle>
              </DialogHeader>
              <DialogDescription v-if="benefit.dialogDesc" class="mb-2">{{
                benefit.dialogDesc
              }}</DialogDescription>
              <p class="whitespace-pre-line">{{ benefit.dialogContent }}</p>
              <p
                v-if="benefit.name == 'Seguro de vida e incapacidad'"
                class="flex items-center gap-2"
              >
                <ExternalLinkIcon class="text-muted-foreground size-4" />
                <a
                  class="text-primary underline"
                  target="_blank"
                  href="/docs/adeslas_general_conditions.pdf"
                >
                  CONDICIONES GENERALES
                </a>
              </p>
            </DialogContent>
          </Dialog>

          <div class="col-span-2">
            <div class="border-border flex gap-4 rounded-sm border p-4.5">
              <div>
                <p class="text-primary mb-2 text-3xl font-medium">
                  198<span class="text-base">€/año</span>
                </p>
                <p class="text-xl font-bold opacity-80">Cesta de navidad</p>
                <p class="text-muted-foreground text-sm">
                  Un detalle para celebrar juntos todo lo que hemos logrado
                </p>
              </div>

              <div class="aspect-square size-30 rounded-sm bg-[#eeefff]">
                <img class="rounded-sm" src="/gifts.png?url" />
              </div>
            </div>

            <a
              href="https://www.caixabank.es/particular/colectivos/empresas-grupo-caixabank.html"
              target="_blank"
              rel="noopener noreferrer"
              class="mx-auto mt-4 flex w-full cursor-pointer items-center justify-center rounded-sm border border-[#009FE2] bg-[#009FE2]/10 p-2 text-[#009FE2]"
            >
              <img
                src="/comercia_vertical.webp"
                alt="Commercia Vertical"
                class="size-8 object-contain"
              />

              <p class="ml-2">Beneficios Grupo CaixaBank</p>

              <ArrowRightIcon class="ml-auto size-6 text-[#009FE2]" />
            </a>
          </div>
        </div>

        <div class="mt-8">
          <p class="text-muted-foreground mt-4">Añade más beneficios</p>

          <div class="mt-2 grid grid-cols-4 gap-4">
            <BenefitAddedCard
              v-for="(benefit, index) in newBenefits"
              :key="index"
              color="#ed9a60"
              :name="benefit.name"
              :type="benefit.type"
              :status="benefit.status"
              :path="benefit.path"
              situational
            >
              <template #icon>
                <component :is="benefit.icon" class="size-4" />
              </template>
            </BenefitAddedCard>
          </div>
        </div>

        <div class="mt-8">
          <p class="text-muted-foreground mt-4 flex gap-2">
            Optimiza tu salario: Plan de Compensación Flexible
            <Button
              variant="ghost"
              size="icon"
              class="text-primary -mt-1 -ml-3 cursor-pointer p-0 hover:bg-transparent"
              @click="showSalaryHint = !showSalaryHint"
            >
              <InfoIcon class="text-primary size-3.5" />
            </Button>
          </p>

          <div class="mt-2 grid grid-cols-4 gap-4">
            <BenefitAddedCard
              v-for="(benefit, index) in salaryBenefits"
              :key="index"
              color="#e78ac7"
              :name="benefit.name"
              :type="benefit.type"
              :status="benefit.status"
              :path="benefit.path"
              situational
            >
              <template #icon>
                <component :is="benefit.icon" class="size-4" />
              </template>
            </BenefitAddedCard>
          </div>
        </div>

        <SalaryHint v-if="showSalaryHint" class="mt-4" />

        <div class="mt-8">
          <p class="text-muted-foreground mt-4">+Descuentos</p>

          <div class="mt-2 grid grid-cols-4 gap-4">
            <BenefitAddedCard
              v-for="(benefit, index) in discountBenefits"
              :key="index"
              color="#32c1c4"
              :name="benefit.name"
              :status="benefit.status"
              :path="benefit.path"
              situational
            >
              <template #icon>
                <component :is="benefit.icon" class="size-4" />
              </template>
            </BenefitAddedCard>
          </div>
        </div>

        <div class="mt-8">
          <p class="text-muted-foreground mt-4">
            Conoce los programas de reconocimiento
          </p>

          <div class="mt-2 grid grid-cols-4 gap-4">
            <Dialog v-for="(benefit, index) in awardsBenefits" :key="index">
              <DialogTrigger class="h-full cursor-help">
                <BenefitAddedCard
                  class="pointer-events-none"
                  color="#a8c267"
                  :status="benefit.status"
                  :price="benefit.price"
                  :name="benefit.name"
                  :type="benefit.type"
                >
                  <template #icon>
                    <component :is="benefit.icon" class="size-4" />
                  </template>
                </BenefitAddedCard>
              </DialogTrigger>

              <DialogContent class="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle class="text-primary text-lg">{{
                    benefit.name
                  }}</DialogTitle>
                </DialogHeader>
                <DialogDescription v-if="benefit.dialogDesc" class="mb-2">{{
                  benefit.dialogDesc
                }}</DialogDescription>
                <p class="whitespace-pre-line">{{ benefit.dialogContent }}</p>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <Heading
        class="mt-10"
        primary-color
        title="Tu Camino Retributivo 2021-2025"
      />
      <SalaryProgress :is-blurred="isBlurred" />

      <!--<div id="faq" class="mt-10">
        <div class="flex items-center gap-2">
          <InfoIcon class="text-primary" />
          <Heading primary-color title="Preguntas frecuentes" />
        </div>

        <div class="mt-4 flex flex-col gap-4">
          <div
            v-for="(item, index) in faq"
            :key="index"
            :ref="(el) => el && (faqRefs[index] = el as HTMLElement)"
            class="flex flex-col gap-2 rounded-sm transition-all duration-300"
          >
            <p
              class="text-primary w-fit text-lg font-medium transition-all duration-300"
              :class="{
                highlighted: highlightedIndex === index,
              }"
            >
              {{ item.title }}
            </p>

            <p class="whitespace-pre-line">
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>-->
    </div>
  </AppLayout>
</template>

<style scoped>
.highlighted {
  background-color: #fef3c7;
}
</style>
